import { useState } from "react";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("New password must be at least 8 characters");
    if (next !== confirm) return setErr("Passwords do not match");
    setLoading(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Please log in again.");
      setOpen(false);
      setTimeout(() => logout(), 400);
    } catch (e2) {
      setErr(formatApiError(e2, "Could not change password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-white/15 bg-transparent hover:bg-white/5 text-white"
          data-testid="admin-change-password-open-btn"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Password
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0A0F1C] border border-white/10 text-white sm:max-w-[440px]"
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold">
            Change password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            You'll be signed out after updating.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Current password
            </Label>
            <Input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 h-11 bg-white/5 border-white/10 text-white"
              data-testid="cp-current"
              required
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              New password
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2 h-11 bg-white/5 border-white/10 text-white"
              data-testid="cp-next"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Confirm new password
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 h-11 bg-white/5 border-white/10 text-white"
              data-testid="cp-confirm"
              minLength={8}
              required
            />
          </div>

          {err && (
            <div className="text-xs text-red-400" data-testid="cp-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] text-white"
            data-testid="cp-submit"
          >
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
